import { HoverableLinkSizes } from '@paywithextend/components'
import cn from 'classnames'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import CodeInput from 'ext-ui/components/CodeInput/CodeInput'
import React, { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import CardsIllustration from 'src/assets/images/cards-illustration.png'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { SupportedBrands } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import { formatPhoneNumber } from 'src/utils/string.utils'

import style from './VerifyPhoneModal.style'

type VerifyPhoneModalProps = {
  className?: string
  isOpen: boolean
  phoneNumber: string
  isVerifying: boolean
  verifyError?: string
  resendError?: string
  verificationResent: boolean
  onVerify: (code: string) => void
  onResend: () => void
  onEditPhone?: () => void
}

export default function VerifyPhoneModal(
  props: VerifyPhoneModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    phoneNumber,
    isVerifying,
    verifyError,
    resendError,
    verificationResent,
    onVerify,
    onResend,
    onEditPhone,
  } = props

  const translate = useTranslate()
  const hideImages = useSelector(selectBrandName) === SupportedBrands.Jpm

  const [code, setCode] = useState('')

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onVerify(code)
  }

  const formattedPhone = phoneNumber ? formatPhoneNumber(phoneNumber) : ''

  return (
    <BaseModal
      className={cn('VerifyPhoneModal', className)}
      isOpen={isOpen}
      onClose={() => {}}
      showCloseButton={false}
      css={style}
      aria-label={translate('Verify Phone Number')}
    >
      {!hideImages && (
        <img
          className="verify-phone-decorative-image"
          src={CardsIllustration}
          alt=""
          role="presentation"
        />
      )}
      <form
        className="verify-phone-form"
        onSubmit={handleSubmit}
        data-testid="verifyPhoneModalForm"
      >
        <h2 className="verify-phone-title">
          <Trns source="Verify Phone Number" />
        </h2>

        <p className="verify-phone-subtitle">
          <Trns source="Enter the 6-digit code sent to your phone number." />
        </p>

        {(formattedPhone || onEditPhone) && (
          <div className="verify-phone-display">
            <span className="verify-phone-display-label">
              <Trns source="Phone Number" />
            </span>
            <span className="verify-phone-display-value">
              {formattedPhone}
              {onEditPhone && (
                <HoverableLink
                  underline
                  size={HoverableLinkSizes.Medium}
                  onClick={onEditPhone}
                  ariaLabelSource="Edit"
                  testId="verifyPhoneEditLink"
                >
                  <Trns source="Edit" />
                </HoverableLink>
              )}
            </span>
          </div>
        )}

        <CodeInput
          label={translate('Verification Code')}
          value={code}
          onChange={setCode}
          errorText={verifyError}
          disabled={isVerifying}
          testId="verifyPhoneCode"
        />

        <Button
          id="verifyPhoneModalBtn"
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          text={<Trns source="Verify Phone Number" />}
          loading={isVerifying}
          disabled={isVerifying || code.length < 6}
          data-testid="verifyPhoneModalBtn"
          big
        />

        <div className="verify-phone-actions">
          <HoverableLink
            underline
            size={HoverableLinkSizes.Medium}
            onClick={onResend}
            ariaLabelSource="Send New Code"
            testId="verifyPhoneSendNewCode"
          >
            <Trns source="Send New Code" />
          </HoverableLink>

          <div className="verify-phone-feedback">
            {verificationResent && (
              <span role="status">
                <Trns source="New code sent" />
              </span>
            )}
            {resendError && <span role="alert">{resendError}</span>}
          </div>
        </div>
      </form>
    </BaseModal>
  )
}
